/* import __COLOCATED_TEMPLATE__ from './handoff-to-external-ticket-on-resolution.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import {
  StandalonePlatformToIcon,
  type StandalonePlatform,
} from 'embercom/objects/standalone/constants';
import { action } from '@ember/object';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { capitalize } from '@ember/string';
import { tracked } from '@glimmer/tracking';
import { type SalesforceCaseDescriptionSource } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import { BlocksDocument } from '@intercom/composer-ember';
import createFragmentFromBlock from 'embercom/lib/blocks/create-fragment-from-block';
import { type Block } from '@intercom/interblocks.ts';
import type Store from '@ember-data/store';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    step: $TSFixMe;
    isReadOnly: boolean;
    editorState: EditorState;
  };
}

export default class HandoffToExternalTicketOnResolution extends Component<Signature> {
  @service declare appService: { app: $TSFixMe };
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;
  @tracked showConfigSheet = false;

  get platformType(): string | undefined {
    return this.appService.app.standalone_platforms?.firstObject?.platform_type;
  }

  get platformName(): string {
    return this.platformType ? capitalize(this.platformType) : '';
  }

  get platformIcon(): InterfaceIconName {
    return (
      (this.platformType && StandalonePlatformToIcon[this.platformType as StandalonePlatform]) ||
      'ticket'
    );
  }

  get platformTranslationPath(): string {
    return `operator.workflows.visual-builder.handoff-to-external-ticket-on-resolution.${this.platformType}`;
  }

  get isToggleDisabled(): boolean {
    if (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      this.salesforceConfig.isPendingSetup
    ) {
      return true;
    }

    return this.args.isReadOnly;
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforcePlatform() {
    // eslint-disable-next-line @intercom/intercom/no-direct-salesforce-platform-access -- This still needs a refactor
    return this.finStandaloneService.salesforcePlatform;
  }

  get handoffEnabled(): boolean {
    return Boolean(this.args.step.handoffToExternalTicketOnResolutionData?.handoffEnabled);
  }

  get handoffOnIdleEnabled(): boolean {
    return Boolean(this.args.step.handoffToExternalTicketOnResolutionData?.handoffOnIdleEnabled);
  }

  get salesforceCaseSubjectBlocks(): BlocksDocument {
    let blocks = new BlocksDocument(
      this.args.step.handoffToExternalTicketOnResolutionData?.salesforceCaseSubjectBlocks?.toArray() ||
        [],
    );
    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Case from Fin AI Agent with {{full_name}}',
        }),
      ]);
    }
    return blocks;
  }

  get salesforceCaseDescriptionBlocks(): BlocksDocument {
    let blocks = new BlocksDocument(
      this.args.step.handoffToExternalTicketOnResolutionData?.salesforceCaseDescriptionBlocks?.toArray() ||
        [],
    );
    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Created from Fin AI Agent',
        }),
      ]);
    }
    return blocks;
  }

  private shouldShowDefaultBlocks(blocks: BlocksDocument) {
    if (blocks.blocks.length === 0) {
      return true;
    }

    if (blocks.blocks.length === 1) {
      let block = blocks.blocks[0];
      return 'text' in block && typeof block.text === 'string' && block.text.trim() === '';
    }

    return false;
  }

  private maybeInitHandoffDataFragment() {
    this.args.step.handoffToExternalTicketOnResolutionData ??= this.args.step.store.createFragment(
      'operator/fin/handoff-to-external-ticket-on-resolution-data',
    );
  }

  @action
  toggleConfigSheet() {
    this.showConfigSheet = !this.showConfigSheet;
  }

  @action
  setHandoffToSalesforceOwnerId(handoffId: string) {
    this.maybeInitHandoffDataFragment();
    this.args.step.handoffToExternalTicketOnResolutionData.handoffToSalesforceOwnerId = handoffId;
  }

  @action
  toggleHandoffToExternalTicketOnResolution() {
    this.maybeInitHandoffDataFragment();
    this.args.step.handoffToExternalTicketOnResolutionData.handoffEnabled =
      !this.args.step.handoffToExternalTicketOnResolutionData.handoffEnabled;
  }

  @action
  toggleHandoffToExternalTicketOnIdle() {
    this.maybeInitHandoffDataFragment();
    this.args.step.handoffToExternalTicketOnResolutionData.handoffOnIdleEnabled =
      !this.args.step.handoffToExternalTicketOnResolutionData.handoffOnIdleEnabled;
  }

  @action
  setCaseDescriptionSource(source: SalesforceCaseDescriptionSource) {
    this.maybeInitHandoffDataFragment();
    this.args.step.handoffToExternalTicketOnResolutionData.salesforceCaseDescriptionSource = source;
  }

  @action
  onCaseSubjectChange(blockDocuments: BlocksDocument) {
    this.maybeInitHandoffDataFragment();
    let blockFragments = blockDocuments.blocks.map((block: Block) =>
      createFragmentFromBlock(this.store, block),
    );

    this.args.step.handoffToExternalTicketOnResolutionData.salesforceCaseSubjectBlocks?.clear();
    this.args.step.handoffToExternalTicketOnResolutionData.salesforceCaseSubjectBlocks?.pushObjects(
      blockFragments,
    );
  }

  @action
  onCaseDescriptionChange(blockDocuments: BlocksDocument) {
    this.maybeInitHandoffDataFragment();
    let blockFragments = blockDocuments.blocks.map((block: Block) =>
      createFragmentFromBlock(this.store, block),
    );

    this.args.step.handoffToExternalTicketOnResolutionData.salesforceCaseDescriptionBlocks?.clear();
    this.args.step.handoffToExternalTicketOnResolutionData.salesforceCaseDescriptionBlocks?.pushObjects(
      blockFragments,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Sections::HandoffToExternalTicketOnResolution': typeof HandoffToExternalTicketOnResolution;
    'workflows/graph-editor/node-items/steps/fin/sections/handoff-to-external-ticket-on-resolution': typeof HandoffToExternalTicketOnResolution;
  }
}
